import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import NotificationContainer from '../Notification/NotificationContainer';

const AdminPromotions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promotions, setPromotions] = useState([]);
  const [filteredPromotions, setFilteredPromotions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    type: 'percentage',
    value: '',
    minimum_order_amount: '',
    usage_limit: '',
    usage_limit_per_user: '1',
    start_date: '',
    end_date: '',
    is_active: true,
    applicable_products: [],
    applicable_categories: []
  });

  // Système de notifications
  const addNotification = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type }]);
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadData();
    loadCategories();
    loadProducts();
  }, []);

  // Ouvrir automatiquement le modal d'ajout si on vient de /admin/promotions/add
  useEffect(() => {
    if (location.pathname === '/admin/promotions/add') {
      if (!loading && promotions.length >= 0) {
        setShowAddModal(true);
        navigate('/admin/promotions', { replace: true });
      }
    }
  }, [location.pathname, loading, promotions.length, navigate]);

  // Fonction pour gérer le tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Appliquer les filtres et le tri
  useEffect(() => {
    let filtered = promotions;

    if (filters.search) {
      filtered = filtered.filter(promotion =>
        promotion.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        (promotion.code && promotion.code.toLowerCase().includes(filters.search.toLowerCase())) ||
        (promotion.description && promotion.description.toLowerCase().includes(filters.search.toLowerCase()))
      );
    }

    if (filters.status) {
      filtered = filtered.filter(promotion => {
        if (filters.status === 'active') {
          return promotion.is_active &&
                 new Date(promotion.start_date) <= new Date() &&
                 new Date(promotion.end_date) >= new Date();
        } else if (filters.status === 'inactive') {
          return !promotion.is_active;
        } else if (filters.status === 'expired') {
          return new Date(promotion.end_date) < new Date();
        }
        return true;
      });
    }

    if (filters.type) {
      filtered = filtered.filter(promotion => promotion.type === filters.type);
    }

    // Appliquer le tri
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'name':
            aValue = (a.name || '').toLowerCase();
            bValue = (b.name || '').toLowerCase();
            break;
          case 'type':
            aValue = a.type || '';
            bValue = b.type || '';
            break;
          case 'value':
            aValue = parseFloat(a.value) || 0;
            bValue = parseFloat(b.value) || 0;
            break;
          case 'start_date':
            aValue = new Date(a.start_date);
            bValue = new Date(b.start_date);
            break;
          case 'end_date':
            aValue = new Date(a.end_date);
            bValue = new Date(b.end_date);
            break;
          case 'used_count':
            aValue = a.used_count || 0;
            bValue = b.used_count || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredPromotions(filtered);
    setCurrentPage(1);
  }, [promotions, filters, sortField, sortOrder]);

  const loadData = async () => {
    try {
      setLoading(true);
      const promotionsData = await adminApi.getPromotions();
      const promotionsList = Array.isArray(promotionsData) ? promotionsData : (promotionsData?.data || []);
      setPromotions(promotionsList);
    } catch (error) {
      console.error('Erreur lors du chargement des promotions:', error);
      addNotification('Erreur lors du chargement des promotions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await adminApi.getCategories();
      const categoriesList = Array.isArray(categoriesData) ? categoriesData : (categoriesData?.data || []);
      setCategories(categoriesList);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const productsData = await adminApi.getProducts();
      const productsList = Array.isArray(productsData) ? productsData : (productsData?.data || []);
      setProducts(productsList);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      type: 'percentage',
      value: '',
      minimum_order_amount: '',
      usage_limit: '',
      usage_limit_per_user: '1',
      start_date: '',
      end_date: '',
      is_active: true,
      applicable_products: [],
      applicable_categories: []
    });
    setEditingPromotion(null);
  };

  const handleEdit = (promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      name: promotion.name || '',
      code: promotion.code || '',
      description: promotion.description || '',
      type: promotion.type || 'percentage',
      value: promotion.value || '',
      minimum_order_amount: promotion.minimum_order_amount || '',
      usage_limit: promotion.usage_limit || '',
      usage_limit_per_user: promotion.usage_limit_per_user || '1',
      start_date: promotion.start_date ? promotion.start_date.split('T')[0] : '',
      end_date: promotion.end_date ? promotion.end_date.split('T')[0] : '',
      is_active: promotion.is_active !== undefined ? promotion.is_active : true,
      applicable_products: promotion.applicable_products || [],
      applicable_categories: promotion.applicable_categories || []
    });
    setShowAddModal(true);
  };

  const handleViewDetails = (promotion) => {
    setSelectedPromotion(promotion);
    setShowDetailsModal(true);
  };

  const handleDeleteClick = (promotion) => {
    setSelectedPromotion(promotion);
    setShowDeleteModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const promotionData = {
        name: formData.name,
        code: formData.code || null,
        description: formData.description || null,
        type: formData.type,
        value: parseFloat(formData.value) || 0,
        minimum_order_amount: formData.minimum_order_amount ? parseFloat(formData.minimum_order_amount) : 0,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        usage_limit_per_user: parseInt(formData.usage_limit_per_user) || 1,
        start_date: formData.start_date,
        end_date: formData.end_date,
        is_active: formData.is_active,
        applicable_products: formData.applicable_products || [],
        applicable_categories: formData.applicable_categories || []
      };

      if (editingPromotion) {
        await adminApi.updatePromotion(editingPromotion.id, promotionData);
        addNotification('Promotion modifiée avec succès', 'success');
      } else {
        await adminApi.createPromotion(promotionData);
        addNotification('Promotion créée avec succès', 'success');
      }

      setShowAddModal(false);
      resetForm();
      await loadData();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde';
      addNotification(errorMessage, 'error');
    }
  };

  const handleDelete = async () => {
    if (!selectedPromotion) return;

    try {
      await adminApi.deletePromotion(selectedPromotion.id);
      addNotification('Promotion supprimée avec succès', 'success');
      setShowDeleteModal(false);
      setSelectedPromotion(null);
      await loadData();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la suppression';
      addNotification(errorMessage, 'error');
    }
  };

  const toggleStatus = async (promotion) => {
    try {
      await adminApi.updatePromotion(promotion.id, {
        ...promotion,
        is_active: !promotion.is_active
      });
      addNotification(`Promotion ${promotion.is_active ? 'désactivée' : 'activée'} avec succès`, 'success');
      await loadData();
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      addNotification('Erreur lors du changement de statut', 'error');
    }
  };

  const formatPrice = (value) => {
    if (!value && value !== 0) return '0';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '0';
    return numValue.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getTypeLabel = (type) => {
    const types = {
      'percentage': 'Pourcentage',
      'fixed_amount': 'Montant fixe',
      'free_delivery': 'Livraison gratuite',
      'buy_x_get_y': 'Acheter X obtenir Y'
    };
    return types[type] || type;
  };

  const getStatusBadge = (promotion) => {
    const now = new Date();
    const startDate = new Date(promotion.start_date);
    const endDate = new Date(promotion.end_date);

    if (!promotion.is_active) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Inactive</span>;
    }

    if (now < startDate) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">À venir</span>;
    }

    if (now > endDate) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Expirée</span>;
    }

    return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</span>;
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPromotions = filteredPromotions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPromotions.length / itemsPerPage);

  if (loading) {
    return (
      <div className="fade-in">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <p className="mt-4 text-gray-500">Chargement des promotions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <NotificationContainer notifications={notifications} removeNotification={removeNotification} />

      <div className="breadcrumb">
        <a href="/admin" className="breadcrumb-item">Dashboard</a>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Produits</span>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Promotions</span>
      </div>

      <div className="page-header">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="page-title">Gestion des Promotions</h1>
            <p className="page-subtitle">
              Créez et gérez les promotions et réductions
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="btn btn-primary"
          >
            <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Ajouter une promotion
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="table-container mb-4">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
              <input
                type="text"
                placeholder="Nom, code..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="modal-form-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="modal-form-select"
              >
                <option value="">Tous</option>
                <option value="active">Actives</option>
                <option value="inactive">Inactives</option>
                <option value="expired">Expirées</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="modal-form-select"
              >
                <option value="">Tous</option>
                <option value="percentage">Pourcentage</option>
                <option value="fixed_amount">Montant fixe</option>
                <option value="free_delivery">Livraison gratuite</option>
                <option value="buy_x_get_y">Acheter X obtenir Y</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="table-container">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th>
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center space-x-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <span>Nom</span>
                    {sortField === 'name' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('type')}
                    className="flex items-center space-x-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <span>Type</span>
                    {sortField === 'type' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('value')}
                    className="flex items-center space-x-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <span>Valeur</span>
                    {sortField === 'value' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('start_date')}
                    className="flex items-center space-x-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <span>Date début</span>
                    {sortField === 'start_date' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('end_date')}
                    className="flex items-center space-x-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <span>Date fin</span>
                    {sortField === 'end_date' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('used_count')}
                    className="flex items-center space-x-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <span>Utilisations</span>
                    {sortField === 'used_count' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentPromotions.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    Aucune promotion trouvée
                  </td>
                </tr>
              ) : (
                currentPromotions.map((promotion) => (
                  <tr key={promotion.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{promotion.name}</div>
                      {promotion.code && (
                        <div className="text-sm text-gray-500">Code: {promotion.code}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{getTypeLabel(promotion.type)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {promotion.type === 'percentage'
                          ? `${promotion.value}%`
                          : promotion.type === 'free_delivery'
                          ? 'Gratuit'
                          : `${formatPrice(promotion.value)} FCFA`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(promotion.start_date)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(promotion.end_date)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {promotion.used_count || 0}
                        {promotion.usage_limit && ` / ${promotion.usage_limit}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(promotion)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleViewDetails(promotion)}
                          className="text-blue-600 hover:text-blue-900"
                          style={{ backgroundColor: '#3b82f6', color: 'white', padding: '6px 12px', borderRadius: '6px' }}
                          title="Détails"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleEdit(promotion)}
                          className="text-orange-600 hover:text-orange-900"
                          style={{ backgroundColor: '#f97316', color: 'white', padding: '6px 12px', borderRadius: '6px' }}
                          title="Modifier"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => toggleStatus(promotion)}
                          className={promotion.is_active ? "text-gray-600 hover:text-gray-900" : "text-green-600 hover:text-green-900"}
                          style={{
                            backgroundColor: promotion.is_active ? '#6b7280' : '#10b981',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '6px'
                          }}
                          title={promotion.is_active ? "Désactiver" : "Activer"}
                        >
                          {promotion.is_active ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteClick(promotion)}
                          className="text-red-600 hover:text-red-900"
                          style={{ backgroundColor: '#ef4444', color: 'white', padding: '6px 12px', borderRadius: '6px' }}
                          title="Supprimer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredPromotions.length)} sur {filteredPromotions.length} promotions
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <span className="px-4 py-2 text-sm font-medium text-gray-700">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal d'ajout/modification */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); resetForm(); }}>
          <div className="modal-content max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingPromotion ? 'Modifier la promotion' : 'Ajouter une promotion'}</h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => { setShowAddModal(false); resetForm(); }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              <div className="space-y-4">
                <div className="modal-form-group">
                  <label className="modal-form-label">Nom de la promotion *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="modal-form-input"
                    required
                  />
                </div>

                <div className="modal-form-group">
                  <label className="modal-form-label">Code promo (optionnel)</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="modal-form-input"
                    placeholder="Ex: PROMO2024"
                  />
                </div>

                <div className="modal-form-group">
                  <label className="modal-form-label">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="modal-form-input"
                    rows="3"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="modal-form-group">
                    <label className="modal-form-label">Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="modal-form-select"
                      required
                    >
                      <option value="percentage">Pourcentage</option>
                      <option value="fixed_amount">Montant fixe</option>
                      <option value="free_delivery">Livraison gratuite</option>
                      <option value="buy_x_get_y">Acheter X obtenir Y</option>
                    </select>
                  </div>

                  <div className="modal-form-group">
                    <label className="modal-form-label">
                      Valeur * {formData.type === 'percentage' && '(%)'}
                      {formData.type === 'fixed_amount' && '(FCFA)'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                      className="modal-form-input"
                      required
                      disabled={formData.type === 'free_delivery'}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="modal-form-group">
                    <label className="modal-form-label">Montant minimum de commande (FCFA)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.minimum_order_amount}
                      onChange={(e) => setFormData({ ...formData, minimum_order_amount: e.target.value })}
                      className="modal-form-input"
                    />
                  </div>

                  <div className="modal-form-group">
                    <label className="modal-form-label">Limite d'utilisation totale</label>
                    <input
                      type="number"
                      value={formData.usage_limit}
                      onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                      className="modal-form-input"
                      placeholder="Illimité si vide"
                    />
                  </div>
                </div>

                <div className="modal-form-group">
                  <label className="modal-form-label">Limite d'utilisation par utilisateur</label>
                  <input
                    type="number"
                    value={formData.usage_limit_per_user}
                    onChange={(e) => setFormData({ ...formData, usage_limit_per_user: e.target.value })}
                    className="modal-form-input"
                    min="1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="modal-form-group">
                    <label className="modal-form-label">Date de début *</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="modal-form-input"
                      required
                    />
                  </div>

                  <div className="modal-form-group">
                    <label className="modal-form-label">Date de fin *</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="modal-form-input"
                      required
                    />
                  </div>
                </div>

                <div className="modal-form-group">
                  <label className="modal-form-label">Produits applicables</label>
                  <select
                    multiple
                    value={formData.applicable_products}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                      setFormData({ ...formData, applicable_products: selected });
                    }}
                    className="modal-form-select"
                    size="5"
                  >
                    <option value="">Tous les produits</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Maintenez Ctrl/Cmd pour sélectionner plusieurs produits</p>
                </div>

                <div className="modal-form-group">
                  <label className="modal-form-label">Catégories applicables</label>
                  <select
                    multiple
                    value={formData.applicable_categories}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                      setFormData({ ...formData, applicable_categories: selected });
                    }}
                    className="modal-form-select"
                    size="5"
                  >
                    <option value="">Toutes les catégories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Maintenez Ctrl/Cmd pour sélectionner plusieurs catégories</p>
                </div>

                <div className="modal-form-group">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Promotion active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingPromotion ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de détails */}
      {showDetailsModal && selectedPromotion && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Détails de la promotion</h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setShowDetailsModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900">{selectedPromotion.name}</h4>
                  {selectedPromotion.code && (
                    <p className="text-sm text-gray-600">Code: {selectedPromotion.code}</p>
                  )}
                </div>

                {selectedPromotion.description && (
                  <div>
                    <p className="text-sm text-gray-700">{selectedPromotion.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Type</p>
                    <p className="text-sm text-gray-900">{getTypeLabel(selectedPromotion.type)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Valeur</p>
                    <p className="text-sm text-gray-900">
                      {selectedPromotion.type === 'percentage'
                        ? `${selectedPromotion.value}%`
                        : selectedPromotion.type === 'free_delivery'
                        ? 'Gratuit'
                        : `${formatPrice(selectedPromotion.value)} FCFA`}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Date de début</p>
                    <p className="text-sm text-gray-900">{formatDate(selectedPromotion.start_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Date de fin</p>
                    <p className="text-sm text-gray-900">{formatDate(selectedPromotion.end_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Utilisations</p>
                    <p className="text-sm text-gray-900">
                      {selectedPromotion.used_count || 0}
                      {selectedPromotion.usage_limit && ` / ${selectedPromotion.usage_limit}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Statut</p>
                    {getStatusBadge(selectedPromotion)}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  handleEdit(selectedPromotion);
                }}
                className="btn btn-primary"
              >
                Modifier
              </button>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="btn btn-secondary"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && selectedPromotion && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Confirmer la suppression</h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setShowDeleteModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <p className="text-gray-700">
                Êtes-vous sûr de vouloir supprimer la promotion <strong>{selectedPromotion.name}</strong> ?
                Cette action est irréversible.
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="btn"
                style={{ backgroundColor: '#ef4444', color: 'white' }}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPromotions;



